// Tauri API utilities
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from './isTauri';
import { attachHttpStream } from './httpStreamTransport';
import { utf8ByteLength } from './formatBytes';
import { useCertificateStore } from '../store/certificateStore';
import { apibooKey } from '../services/storageNamespace';
import { explainRequestSettingsError } from './requestSettingsErrors';
import { readTempResponseFile } from './tempResponseReader';

// `isTauri` lives in ./isTauri (17.09.2026) so that a module inside the MCP
// import graph can ask "desktop or web?" without importing the HTTP helpers
// below. Re-exported: every existing `import { isTauri } from '.../utils/tauri'`
// keeps working.
export { isTauri };

/**
 * Passkeys are disabled on the macOS build for now — WKWebView refuses to let
 * embedded content assert a relying-party ID it doesn't natively own without
 * the com.apple.developer.web-authentication.relying-party-identifier
 * entitlement, which isn't provisioned yet. See mac_passs_integration.md.
 * Windows (WebView2) and the browser are unaffected.
 */
export const isMacTauri = async () => {
  if (!isTauri()) return false;
  try {
    const { platform } = await import('@tauri-apps/plugin-os');
    return platform() === 'macos';
  } catch {
    return false;
  }
};

/**
 * Does this platform deliver the OS suspend / screen-lock signals the vault's
 * auto-lock listens for?
 *
 * Only Windows does today. `src-tauri/src/vault_lock_platform.rs` implements a
 * message-only window there (PBT_APMSUSPEND + WTS_SESSION_LOCK); its macOS and
 * Linux `platform::start()` are stubs that log and register nothing, so no
 * signal is ever produced and the two toggles that depend on them would sit
 * there switched on, saving and reloading perfectly, protecting nothing. The
 * idle timeout is unaffected — that timer is pure Rust and runs everywhere.
 *
 * KEEP THIS IN STEP WITH THAT MODULE. When a platform grows real hooks, adding
 * it here belongs in the same change; the stubs name the exact APIs
 * (NSWorkspaceWillSleepNotification / com.apple.screenIsLocked on macOS,
 * logind PrepareForSleep / org.freedesktop.ScreenSaver on Linux).
 *
 * Answers `true` when the platform cannot be determined, deliberately: that
 * only happens if the os plugin fails to load, and the failure mode of a wrong
 * `false` is a Windows user unable to turn a working protection OFF, while a
 * wrong `true` is only ever reached on a machine where the import already
 * broke. The lock itself is decided in Rust regardless of what this returns.
 */
export const hasOsLockSignals = async () => {
  if (!isTauri()) return false;
  try {
    const { platform } = await import('@tauri-apps/plugin-os');
    return platform() === 'windows';
  } catch {
    return true;
  }
};

/**
 * Proxy storage keys. The transport (which proxy this machine goes through) is
 * a property of the network and stays device-global; the credentials used to
 * authenticate to it identify a PERSON and are namespaced per account, like
 * every other credential. See ProxySettings.js for the full rationale.
 */
export const PROXY_TRANSPORT_KEY = 'apiboo-proxy-transport';
export const proxyCredentialsKey = () => apibooKey('proxy-credentials');
export const proxyPasswordSessionKey = () => apibooKey('proxy-password');

// The password itself is dropped at sign-out by clearAccountNamespace() —
// the one teardown that already owns every account-scoped key.

/**
 * Read the saved proxy config: device-global transport + this account's
 * credentials. Returns null if proxy is disabled or not configured.
 */
function getProxyConfig() {
  try {
    const raw = localStorage.getItem(PROXY_TRANSPORT_KEY);
    if (!raw) return null;
    const config = JSON.parse(raw);
    if (!config || !config.enabled) return null;

    const proxyType = config.type || 'http';
    const host = (config.host || '').trim();

    // Enabled but no host → invalid config; skip proxy rather than failing every request.
    if (proxyType !== 'system' && !host) return null;

    let username = null;
    try {
      const rawCreds = localStorage.getItem(proxyCredentialsKey());
      username = (rawCreds ? JSON.parse(rawCreds)?.username : null) || null;
    } catch {
      username = null;
    }
    let password = null;
    try {
      password = sessionStorage.getItem(proxyPasswordSessionKey()) || null;
    } catch {
      password = null;
    }

    return {
      enabled: true,
      proxy_type: proxyType,
      host,
      port: Number(config.port) || 8080,
      username,
      password,
      no_proxy: config.noProxy || null,
    };
  } catch {
    return null;
  }
}

/**
 * Hand a user-picked upload path to the Rust session allowlist (COL-01).
 *
 * `send_http_request` reads `multipart[].file_path` off the disk, and a
 * form-data row's path can legitimately point anywhere — the user chose it in
 * an OS dialog. The identical field is also written by the collection
 * importers and by shared-workspace sync, where the path is whatever the
 * document's author typed. Nothing about the path itself separates those two,
 * so the trust decision is made HERE, at the picker, and recorded in Rust
 * process memory that a document cannot write to. Any `file_path` the backend
 * has not been told about is refused at send time.
 *
 * Returns the canonicalized path Rust registered. Throws if registration
 * fails, so a caller must not store a path it could not register — that would
 * leave a row that looks ready and fails on Send.
 */
export const registerUploadPath = async (path) => {
  if (!isTauri()) return path;
  return invoke('register_upload_path', { path });
};

/**
 * Force using Tauri HTTP client (for development/testing)
 */
export const forceTauriMode = () => {
  window.__FORCE_TAURI__ = true;
};

/**
 * Check if Tauri mode is forced
 */
const isForcedTauriMode = () => {
  return window.__FORCE_TAURI__ === true;
};

/**
 * Send HTTP request using Tauri's backend.
 * Automatically includes proxy config from localStorage if enabled.
 */
export const sendHttpRequest = async (requestData) => {
  // Opt-OUT switches (22.08.2026, AI fetch_url): a request to a URL the
  // model chose must not carry the user's identity. Every other caller is
  // unchanged — both default to attached, exactly as before.
  const proxy = requestData.attachProxy === false ? null : getProxyConfig();

  try {
    const { clientCerts, caCerts, verifySSL } = useCertificateStore.getState();
    const tls = requestData.attachClientCerts === false
      ? { client_certs: [], ca_certs: [], verify_ssl: true }
      : {
        client_certs: clientCerts.map((c) => ({
          domain: c.domain,
          cert_type: c.type,
          cert_path: c.certPath,
          key_path: c.keyPath || null,
          passphrase: c.passphrase || null,
        })),
        ca_certs: caCerts.map((c) => c.path),
        // "SSL certificate verification: off" is about the USER's requests
        // only — the ones that carry `network` (withUserNetwork). The backend,
        // AI providers, updates and avatars are always verified. Rust enforces
        // the same rule and is the authority (request_preferences.rs); this is
        // the mirror, so the payload never even asks.
        verify_ssl: requestData.network ? verifySSL : true,
      };

    // "If the answer is an event stream, stream it to me" — see utils/httpStreamTransport.js
    const attached = requestData.stream?.id ? await attachHttpStream(requestData.stream) : null;
    let response;
    try {
      response = await invoke('send_http_request', {
      request: {
        method: requestData.method,
        url: requestData.url,
        headers: requestData.headers || {},
        body: requestData.multipart?.length ? null : requestData.body || null,
        multipart: requestData.multipart?.length ? requestData.multipart : null,
        proxy: proxy,
        tls,
        // Optional per-request override of the Rust client's 30s total timeout
        // (clamped 1–600s in lib.rs). Used by the AI provider adapters, whose
        // non-streamed model turns routinely outlive 30s. Omitted/undefined
        // keeps the default for every other caller.
        timeout_secs: requestData.timeout_secs ?? null,
        stream_id: attached ? requestData.stream.id : null,
        // Settings → Requests / Editor → Max response size, passed ONLY by the
        // paths that send the user's own requests (see requestNetworkOptions).
        // Absent: every field is null and Rust keeps its previous behaviour.
        timeout_ms: requestData.network?.timeout_ms ?? null,
        follow_redirects: requestData.network?.follow_redirects ?? null,
        http_version: requestData.network?.http_version ?? null,
        max_response_bytes: requestData.network?.max_response_bytes ?? null,
      },
      });
    } catch (error) {
      attached?.settle(null);
      throw error;
    }
    attached?.settle(response);

    // Large responses are written to a temp file by Rust to avoid the WebView2
    // IPC message-size limit.
    // Read them back here in safe-sized chunks (utils/tempResponseReader.js).
    if (response.temp_file) {
      const body = await readTempResponseFile(invoke, response.temp_file, {
        binary: response.temp_file_binary === true,
        isCancelled: requestData.isCancelled,
      });
      if (body.cancelled) {
        response.data = ''; // the caller dropped this request; nobody reads the body
      } else if (body.base64 != null) {
        response.data = '';
        response.data_base64 = body.base64;
      } else {
        response.data = body.text;
      }
    }

    return response;
  } catch (error) {
    throw new Error(humanizeNetworkError(error));
  }
};

/**
 * Turn the Rust/reqwest error string into a short, user-facing message.
 * Falls back to the raw cause (minus reqwest's boilerplate prefix) when the
 * error doesn't match a known category.
 */
function humanizeNetworkError(raw) {
  // Strip reqwest's "error sending request for url (<URL>):" prefix up front so
  // the request URL itself can't trigger a keyword match (e.g. a host named
  // ssl.example.com or dns-test.com); we only classify against the cause.
  const msg = String(raw ?? '')
    .trim()
    .replace(/^error sending request for url \([^)]*\):\s*/i, '');
  // Failures caused by a Settings → Requests / Editor choice first: they name
  // the setting, in the user's language, instead of a generic TLS/connect line.
  const fromSettings = explainRequestSettingsError(raw);
  if (fromSettings) return fromSettings;
  if (/dns error|failed to lookup|nodename nor servname|name or service not known/i.test(msg)) {
    return 'Could not resolve host — check the URL’s domain name.';
  }
  // reqwest nests TLS/handshake and connect-timeout failures inside
  // "error trying to connect: …", so these must be checked before the generic
  // connection branch below or they'd be mislabeled as "server unreachable".
  if (/certificate|handshake|\btls\b|\bssl\b|self.signed/i.test(msg)) {
    return 'TLS error — the server’s certificate could not be verified.';
  }
  if (/timed out|timeout/i.test(msg)) {
    return 'The request timed out.';
  }
  if (/error trying to connect|connection refused|connect(ion)? (error|reset)|tcp connect/i.test(msg)) {
    return 'Could not connect to the server — it may be down or unreachable.';
  }
  return msg || 'Request failed.';
}

/**
 * Fallback to browser fetch for non-Tauri environments
 */
export const sendBrowserRequest = async (requestData) => {
  const { method, url, headers, body, multipart } = requestData;

  const requestOptions = {
    method: method,
    headers: { ...(headers || {}) },
  };

  if (['POST', 'PUT', 'PATCH'].includes(method)) {
    if (multipart?.length) {
      const formData = new FormData();
      for (const part of multipart) {
        if (part.file_path && part.file) {
          formData.append(part.name, part.file, part.file_name || 'file');
        } else if (part.text != null) {
          formData.append(part.name, part.text);
        }
      }
      requestOptions.body = formData;
    } else if (body) {
      requestOptions.body = body;
    }
  }

  const startTime = Date.now();

  try {
    const response = await fetch(url, requestOptions);
    const responseText = await response.text();
    const endTime = Date.now();
    // The body goes on VERBATIM. Parsing JSON here and re-stringifying it for
    // display rounded every integer above 2^53 (9007199254740993 → …992) in
    // what test scripts and history see; the viewer formats for display only
    // (utils/formatCode.formatJson, which keeps number literals as written).
    return {
      status: response.status,
      status_text: response.statusText,
      headers: Object.fromEntries(response.headers.entries()),
      data: responseText,
      time: endTime - startTime,
      size: utf8ByteLength(responseText),
    };
  } catch (error) {
    throw new Error(`Browser request failed: ${error.message}`);
  }
};

/**
 * Send HTTP via the Rust backend only — no browser fallback (avoids CORS).
 * Use for AI tool calls and other external API requests in the desktop app.
 */
export const sendRequestViaTauri = async (requestData) => {
  if (!isTauri() && !isForcedTauriMode()) {
    throw new Error(
      'External API requests require the Apiboo desktop app. Run via Tauri (npm run tauri dev) instead of the browser dev server.'
    );
  }
  assertAbsoluteHttpUrl(requestData?.url);
  return sendHttpRequest(requestData);
};

/**
 * Reject scheme-less / relative URLs before they're sent. A URL like "/test"
 * (commonly the result of an undefined {{baseUrl}} variable) otherwise resolves
 * against the app's own origin: the browser-fetch fallback hits the dev server /
 * webview, which returns the app's index.html with a misleading 200. Previewing
 * that HTML then re-loads the app's own bundle inside the iframe.
 */
const ABSOLUTE_HTTP_URL_RE = /^https?:\/\//i;
function assertAbsoluteHttpUrl(url) {
  const trimmed = (url ?? '').trim();
  if (!ABSOLUTE_HTTP_URL_RE.test(trimmed)) {
    throw new Error(
      `Invalid URL "${trimmed || '(empty)'}": enter a full http:// or https:// address. ` +
        `If you used a base-URL variable, it may be undefined for the selected environment.`
    );
  }
}

/**
 * Universal HTTP request function that automatically chooses Tauri or browser
 */
export const sendRequest = async (requestData) => {
  assertAbsoluteHttpUrl(requestData?.url);
  // Desktop app: the Rust backend is authoritative. Surface its errors directly
  // instead of silently retrying through the webview's fetch — that fallback is
  // CORS-blocked and can hang, leaving the UI stuck on "Sending…".
  if (isTauri() || isForcedTauriMode()) {
    return await sendHttpRequest(requestData);
  }

  return await sendBrowserRequest(requestData);
};
